import { useApolloClient } from '@apollo/client';
import { Box, Button, Flex, Text, useToast, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { DownloadInstanceIconButton } from 'packages/dataPipeline/ui/details/DownloadMenuButton';
import { UploadDrawerForImplementation } from 'packages/dataPipeline/ui/uploads/UploadDrawer';
import { CertificateDownloadIconButton } from '~/backup/shared/CertificateDownloadIconButton';
import { formatPeriodShort } from '~/backup/shared/format';
import {
  inputStatusColor,
  inputStatusLabel,
  periodFilesAction,
  periodFilesActionLabel,
} from '~/backup/shared/servicingPeriods';
import { usePortalRefreshBusServicingPeriodMutation } from '~/backup/shared/views/deal-overview/dealOverview.gql.generated';
import { DealPeriod } from '~/backup/shared/views/deal-overview/types';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

/** The period's inputs that have a recorded upload (a failed one too). */
function periodUploads(
  period: DealPeriod,
): { name: string; artifactInstanceId: string }[] {
  return period.inputs.flatMap((input) =>
    input.rawArtifactInstanceId
      ? [{ name: input.name, artifactInstanceId: input.rawArtifactInstanceId }]
      : [],
  );
}

/** The cycle whose certificate can be downloaded, once it is issued. */
function issuedCertificateCycleId(period: DealPeriod): string | null {
  return period.cycle?.certificateStatus === 'issued' ? period.cycle.id : null;
}

/** One file of a period: its name (and any note) beside its download icon. */
function DocumentRow({
  name,
  note,
  children,
}: {
  name: string;
  note: React.ReactElement | null;
  children: React.ReactElement | null;
}): React.ReactElement {
  return (
    <Flex align="center" justify="space-between" gap="0.5rem" minH="1.5rem">
      <Flex align="baseline" gap="0.5rem">
        <Text fontSize="0.8rem">{name}</Text>
        {note}
      </Flex>
      {children}
    </Flex>
  );
}

/**
 * A period's files in the "All periods" table: the certificate once issued, then
 * every input with a recorded upload, one download icon each.
 */
export function PeriodDocumentsCell({
  period,
}: {
  period: DealPeriod;
}): React.ReactElement {
  const certificateCycleId = issuedCertificateCycleId(period);
  const uploads = periodUploads(period);
  const periodName = formatPeriodShort(period.reportingPeriod);

  if (certificateCycleId === null && uploads.length === 0) {
    return (
      <Text fontSize="0.8rem" color={colors.gray[500]}>
        —
      </Text>
    );
  }
  return (
    <VStack align="stretch" spacing="0.125rem">
      {certificateCycleId !== null && (
        <DocumentRow name="Certificate PDF" note={null}>
          <CertificateDownloadIconButton
            servicingCycleId={certificateCycleId}
            size="xs"
            label={`${periodName} certificate`}
          />
        </DocumentRow>
      )}
      {uploads.map(({ name, artifactInstanceId }) => (
        <DocumentRow key={artifactInstanceId} name={name} note={null}>
          <DownloadInstanceIconButton
            artifactInstanceId={artifactInstanceId}
            size="xs"
            label={`${periodName} ${name}`}
          />
        </DocumentRow>
      ))}
    </VStack>
  );
}

/**
 * The selected period's Documents card: the certificate, then every input the
 * period expects — downloadable once uploaded, else what it waits on — so what
 * is still owed sits beside what has arrived.
 */
export function PeriodDocumentsCard({
  period,
}: {
  period: DealPeriod;
}): React.ReactElement {
  const certificateCycleId = issuedCertificateCycleId(period);
  const periodName = formatPeriodShort(period.reportingPeriod);

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor={colors.gray[100]}
      p="1.5rem"
    >
      <Typography variant="section-title">Documents</Typography>
      <VStack align="stretch" spacing="0.25rem" mt="1rem">
        <DocumentRow
          name="Certificate PDF"
          note={
            certificateCycleId === null ? (
              <Text fontSize="0.75rem" color={colors.gray[500]}>
                Not issued
              </Text>
            ) : null
          }
        >
          {certificateCycleId === null ? null : (
            <CertificateDownloadIconButton
              servicingCycleId={certificateCycleId}
              size="xs"
              label={`${periodName} certificate`}
            />
          )}
        </DocumentRow>
        {period.inputs.map((input) => (
          <DocumentRow
            key={input.name}
            name={input.name}
            note={
              <Text fontSize="0.75rem" color={inputStatusColor(input.status)}>
                {inputStatusLabel(input.status)}
              </Text>
            }
          >
            {input.rawArtifactInstanceId ? (
              <DownloadInstanceIconButton
                artifactInstanceId={input.rawArtifactInstanceId}
                size="xs"
                label={`${periodName} ${input.name}`}
              />
            ) : null}
          </DocumentRow>
        ))}
      </VStack>
    </Box>
  );
}

/**
 * Re-checks the period's inputs against the pipeline. Their statuses otherwise
 * move only on the deliverables cron, so this is how a just-uploaded file shows as
 * received (or failed) right away. Shown only while the period needsRefresh.
 */
export function RefreshButton({
  implementationId,
  reportingPeriod,
}: {
  implementationId: string;
  reportingPeriod: DealPeriod['reportingPeriod'];
}): React.ReactElement {
  const toast = useToast();
  const [refresh, { loading }] = usePortalRefreshBusServicingPeriodMutation({
    variables: { implementationId, reportingPeriod },
    refetchQueries: ['PortalBusServicingPeriods', 'PortalBusServicingCycle'],
    awaitRefetchQueries: true,
    onError: () => {
      toast({
        title: 'Could not refresh the period',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });

  return (
    <Button
      size="sm"
      variant="ghost"
      colorScheme="blue"
      isLoading={loading}
      onClick={(): void => {
        // `onError` already toasts the failure.
        refresh().catch(() => undefined);
      }}
    >
      Refresh
    </Button>
  );
}

/**
 * Opens the drawer on the period's run: its latest version while that is on the
 * latest graph, else the period's next version. One drawer uploads, shows progress
 * and downloads; the button names what the period needs next. Closing it re-reads
 * the periods, so an upload made in it offers a Refresh.
 */
export function FilesButton({
  implementationId,
  period,
}: {
  implementationId: string;
  period: DealPeriod;
}): React.ReactElement {
  const { user } = useUserAuthOrThrow();
  const apolloClient = useApolloClient();
  const [isOpen, setIsOpen] = useState(false);
  const close = (): void => {
    setIsOpen(false);
    // A failed re-read leaves the page as it was; the queries surface their own errors.
    apolloClient
      .refetchQueries({
        include: ['PortalBusServicingPeriods', 'PortalBusServicingCycle'],
      })
      .catch(() => undefined);
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        colorScheme="blue"
        onClick={(): void => setIsOpen(true)}
      >
        {periodFilesActionLabel[periodFilesAction(period)]}
      </Button>
      {isOpen && (
        <UploadDrawerForImplementation
          organizationId={user.organization.id}
          implementationId={implementationId}
          reportingDate={period.reportingPeriod}
          neededInput={null}
          onClose={close}
        />
      )}
    </>
  );
}
